/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from 'react'
import { Box, Grid } from '@mui/material'
import FixedWidthContainer from 'components/FixedWidthContainer'
import TabsComponent from 'pages/MyAccount/TabsComponent'
import UserProfile from 'pages/MyAccount/UserProfile'
import AddressBook from 'pages/MyAccount/AddressBook'
import PaymentMethods from 'pages/MyAccount/PaymentCard'
import AccountForm from 'pages/MyAccount/AccountForm'
import { Order } from 'hooks/useOrder'
import useOrders from 'hooks/useOrder'
import useReturns from 'hooks/useReturns'
import useClaims from 'hooks/useClaims'
import { AuthContext } from 'contexts/AuthContext'
import MyOrder from 'pages/MyAccount/MyOrder'
import Claim from 'pages/MyAccount/Claim'
import { useLocation, useSearchParams } from 'react-router-dom'
import Return from 'pages/MyAccount/Return'
import { useTranslation } from 'react-i18next'

const MyAccountPage = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedTab, setSelectedTab] = useState(0)
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const {
    orders,
    totalPages,
    currentPage,
    setCurrentPage,
    fetchOrders,
    isLoadingOrders
  } = useOrders()
  const {
    returns,
    fetchReturns,
    fetchReturnById,
    sendReturnMessage,
    isLoadingReturns
  } = useReturns()
  const {
    claims,
    fetchClaims,
    fetchClaimById,
    sendClaimMessage,
    isLoadingClaims
  } = useClaims()
  const { accountInfo } = useContext(AuthContext)
  const location = useLocation()

  const tabMapping: { [key: string]: number } = {
    'my-order': 0,
    'address-book': 1,
    'payment-methods': 2,
    'return-order': 3,
    claim: 4
  }

  const reverseTabMapping: { [key: number]: string } = {
    0: 'my-order',
    1: 'address-book',
    2: 'payment-methods',
    3: 'return-order',
    4: 'claim'
  }

  // Keep the selected view in sync with the account URL.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    const editParam = searchParams.get('edit')
    const orderIdParam = searchParams.get('orderId')

    if (editParam === 'profile') {
      setIsEditingProfile(true)
    } else if (tabParam && tabMapping[tabParam] !== undefined) {
      setSelectedTab(tabMapping[tabParam])

      if (orderIdParam && orders.length > 0) {
        const order = orders.find(o => o.id === orderIdParam)
        if (order) {
          setSelectedOrder(order)
        }
      }
    } else if (!tabParam) {
      setSearchParams({ tab: 'my-order' })
      setSelectedTab(0)
    }
  }, [orders, searchParams])

  // Reset transient account-page state on navigation back to the root route.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (location.pathname === '/my-account') {
      const tabParam = searchParams.get('tab')
      const editParam = searchParams.get('edit')
      const orderIdParam = searchParams.get('orderId')

      if (editParam === 'profile' || orderIdParam) {
        return
      }

      if (!tabParam) {
        setSearchParams({ tab: 'my-order' })
        setSelectedTab(0)
      }
      setIsEditingProfile(false)
      setSelectedOrder(null)
    }
  }, [location])

  // Fetch the page selected by the pager.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOrders(currentPage)
  }, [currentPage])

  // Load returns and claims once for the account overview.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchReturns()
    fetchClaims()
  }, [])

  const handleTabClick = (index: number) => {
    setSelectedTab(index)
    const tabName = reverseTabMapping[index]
    if (tabName) {
      setSearchParams({ tab: tabName })
    }
  }

  const handleViewOrderDetails = (order: Order) => {
    setSelectedOrder(order)
    setSearchParams({ tab: 'my-order', orderId: order.id })
  }

  const handleBackToOrders = () => {
    setSelectedOrder(null)
    setSearchParams({ tab: 'my-order' })
  }

  const handleEditProfile = () => {
    setIsEditingProfile(true)
    setSearchParams({ edit: 'profile' })
  }

  const handleProfileCancel = () => {
    setIsEditingProfile(false)
    setSearchParams({ tab: reverseTabMapping[selectedTab] || 'my-order' })
  }

  return (
    <Box>
      <Box
        sx={{
          height: 280,
          background:
            'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)
            `,
            pointerEvents: 'none'
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            color: 'white',
            zIndex: 1
          }}
        >
          <Box
            component='h1'
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 1,
              textShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}
          >
            {t('myAccount.title')}
          </Box>
          <Box
            component='p'
            sx={{
              fontSize: { xs: '0.9rem', md: '1rem' },
              opacity: 0.95,
              textShadow: '0 1px 5px rgba(0,0,0,0.2)'
            }}
          >
            {t('myAccount.subtitle')}
          </Box>
        </Box>
      </Box>

      <FixedWidthContainer>
        <Grid container spacing={4} justifyContent='center'>
          {isEditingProfile ? (
            <Grid item xs={12}>
              <AccountForm onCancel={handleProfileCancel} />
            </Grid>
          ) : (
            <>
              {!selectedOrder && (
                <Grid item xs={12} sm={4}>
                  <UserProfile
                    firstName={accountInfo?.firstName || ''}
                    lastName={accountInfo?.lastName || ''}
                    email={accountInfo?.email || ''}
                    onEditProfile={handleEditProfile}
                    isLoading={!accountInfo}
                  />
                </Grid>
              )}

              <Grid item xs={selectedOrder ? 12 : 8}>
                {!selectedOrder && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%'
                    }}
                  >
                    <TabsComponent
                      selectedTab={selectedTab}
                      onClick={handleTabClick}
                    />
                  </Box>
                )}

                {selectedTab === 0 && (
                  <MyOrder
                    selectedOrder={selectedOrder}
                    orders={orders}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onViewOrderDetails={handleViewOrderDetails}
                    onBackToOrders={handleBackToOrders}
                    onPageChange={setCurrentPage}
                    isLoading={isLoadingOrders}
                  />
                )}

                {selectedTab === 1 && <AddressBook />}

                {selectedTab === 2 && <PaymentMethods />}

                {selectedTab === 3 && (
                  <Return
                    returns={returns}
                    orders={orders}
                    onRefresh={fetchReturns}
                    fetchReturnById={fetchReturnById}
                    sendReturnMessage={sendReturnMessage}
                    isLoading={isLoadingReturns}
                  />
                )}

                {selectedTab === 4 && (
                  <Claim
                    claims={claims}
                    orders={orders}
                    onRefresh={fetchClaims}
                    fetchClaimById={fetchClaimById}
                    sendClaimMessage={sendClaimMessage}
                    isLoading={isLoadingClaims}
                  />
                )}
              </Grid>
            </>
          )}
        </Grid>
      </FixedWidthContainer>
    </Box>
  )
}

export default MyAccountPage
